import { omit } from "lodash";

import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { formatOrder } from "utils/orders.util";

import { emptySplitApi } from "./empty-split-api";
import {
  Order,
  OrderInput,
  OrderItemInput,
  OrderQuery,
  OrderReportItem,
  OrderReportItemsDownloadFilters,
  OrderReportItemsFiltersQuery,
  OrdersCountsResponse,
  OrdersFilters,
  OrdersResponse
} from "./types/orders.type";
import { CSVReportResponse } from "./types/reports.type";

export const DEFAULT_ORDERS_LIMIT = 25;
export const ORDER_DEFAULT_QUERY =
  "{id,created_by,updated_by,clinic{id,name,logo},patient{id,first_name,last_name,title,middle_name,preferred_name,suffix,email,phone},items{id,price,sales_tax,total,created_at,description,model,quantity,tracking_number,checked_in_at,ear,quickbook_id,mfr_invoice_number,manufacturer,patient_product{id,description,extended_warranty_expiration_date,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,notes,serial_number,ear,type,subtype,purchase_date,due_date,delivered_at,exchanged_at,returned_at,inventory_product,order_item,sale_item},inventory_product{id,checked_in_at,quantity,ordered_quantity,reserved_quantity,loanable,demoable,status,quickbook_id,available_quantity,serial_number,ear,condition,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,specification,product{id,specification,display_name,supplier_price,master_product{id,type,subtype,specification}}}},shipping_cost,total,created_at,status,checked_in_by,clinic_order_number,type,supplier_order_number,notes,checked_in_at}";

export const ordersApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<Order[], OrdersFilters>({
      query: ({
        query = ORDER_DEFAULT_QUERY,
        offset = 0,
        clinic = "",
        limit = 100,
        ...params
      }) =>
        `/orders/?ordering=-created_at&limit=${limit}&offset=${offset}&clinic=${clinic}&${formatValidQueryParamUrlCommaSeparated(
          params
        )}&query=${query}`,
      providesTags: ["Orders"],
      transformResponse: (response: OrdersResponse) =>
        response.results.map(formatOrder)
    }),
    getOrdersCount: builder.query<
      number,
      Omit<OrdersFilters, "offset" | "limit" | "query">
    >({
      query: ({ clinic = "", ...params }) =>
        `/orders/count/?clinic=${clinic}&${formatValidQueryParamUrlCommaSeparated(
          params
        )}`,
      providesTags: ["Orders"]
    }),
    getOrdersCounts: builder.query<
      OrdersCountsResponse,
      Omit<
        OrdersFilters,
        "offset" | "limit" | "query" | "status" | "type" | "exclude_type"
      >
    >({
      query: ({ clinic = "", ...params }) =>
        `/orders/counts/?clinic=${clinic}&${formatValidQueryParamUrlCommaSeparated(
          params
        )}`,
      providesTags: ["Orders"]
    }),
    getOrder: builder.query<Order, OrderQuery>({
      query: ({ orderId, query = ORDER_DEFAULT_QUERY }) =>
        `/orders/${orderId}/?query=${query}`,
      providesTags: ["Orders"],
      transformResponse: formatOrder
    }),
    createOrder: builder.mutation<Order, OrderInput>({
      query: (order) => ({
        url: "/orders/",
        method: "POST",
        body: order
      }),
      invalidatesTags: ["Orders", "PatientProducts", "SaleItems"]
    }),
    editOrder: builder.mutation<Order, OrderInput>({
      query: (order) => ({
        url: `/orders/${order.id}/`,
        method: "PATCH",
        body: omit(order, ["id"])
      }),
      invalidatesTags: ["Orders", "PatientProducts", "SaleItems"]
    }),
    checkInOrder: builder.mutation<
      Order,
      {
        shipping_cost: string;
        supplier_order_number: string;
        orderId: number;
        items: Partial<OrderItemInput>[];
      }
    >({
      query: ({ orderId, items, shipping_cost, supplier_order_number }) => ({
        url: `/orders/${orderId}/check-in/`,
        method: "POST",
        body: { shipping_cost, items, supplier_order_number }
      }),
      invalidatesTags: ["Orders", "PatientProducts", "SaleItems"]
    }),
    deleteOrder: builder.mutation<void, number>({
      query: (orderId) => ({
        url: `/orders/${orderId}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["Orders", "PatientProducts", "SaleItems"]
    }),
    postQuickbooksOrder: builder.mutation<any, number>({
      query: (order) => ({
        url: `/orders/${order}/sync/`,
        method: "POST"
      }),
      invalidatesTags: ["Orders"]
    }),
    getOrderItems: builder.query<
      OrderReportItem[],
      OrderReportItemsFiltersQuery
    >({
      query: ({ query = "{*}", offset = 0, limit = 25, ...params }) =>
        `/order-items/?limit=${limit}&offset=${offset}&${formatValidQueryParamUrlCommaSeparated(
          params
        )}&query=${query}`,
      providesTags: ["OrderItems"]
    }),
    downloadOrderItemsCSV: builder.query<
      CSVReportResponse,
      OrderReportItemsDownloadFilters
    >({
      query: ({ query = "{*}", ...filters }) => ({
        url: `/order-items/?query=${query}&${formatValidQueryParamUrlCommaSeparated(
          { ...filters }
        )}`,
        headers: { Accept: "custom-csv/json" }
      })
    })
  })
});

export const {
  useGetOrdersQuery,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useEditOrderMutation,
  useCheckInOrderMutation,
  useLazyGetOrdersCountQuery,
  useLazyGetOrdersCountsQuery,
  useDeleteOrderMutation,
  usePostQuickbooksOrderMutation,
  useGetOrderItemsQuery,
  useLazyDownloadOrderItemsCSVQuery,
  useCreateOrderMutation
} = ordersApi;
